/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/compliance.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * The closed vocabulary of what the platform can substantiate. Each member names an existing
 * capability with a collector behind it; a control naming anything else is refused at
 * registration, because a control demanding evidence nothing can collect would read as
 * satisfied-by-absence in every pack.
 */
export type EvidenceKind = 'audit_chain_integrity' | 'change_approvals' | 'remediation_governance' | 'incident_response' | 'pii_classification' | 'quality_scoring';

export const EVIDENCE_KINDS: readonly EvidenceKind[] = ['audit_chain_integrity', 'change_approvals', 'remediation_governance', 'incident_response', 'pii_classification', 'quality_scoring'];

export function isEvidenceKind(value: unknown): value is EvidenceKind {
  return (['audit_chain_integrity', 'change_approvals', 'remediation_governance', 'incident_response', 'pii_classification', 'quality_scoring'] as readonly unknown[]).includes(value);
}

export interface Control {
  readonly code: string;
  readonly title: string;
  readonly description?: string;
  readonly evidence: readonly EvidenceKind[];
}

export function isControl(value: unknown): value is Control {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['code'] === 'string' &&
    typeof candidate['title'] === 'string' &&
    Array.isArray(candidate['evidence']) &&
    (candidate['evidence'] as readonly unknown[]).every((item) => isEvidenceKind(item))
  );
}

export interface Framework {
  readonly code: string;
  readonly version: string;
  readonly title: string;
  readonly description: string;
  readonly registered_by: string;
  readonly registered_at: string;
  readonly controls: readonly Control[];
}

export function isFramework(value: unknown): value is Framework {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['code'] === 'string' &&
    typeof candidate['version'] === 'string' &&
    typeof candidate['title'] === 'string' &&
    typeof candidate['description'] === 'string' &&
    typeof candidate['registered_by'] === 'string' &&
    typeof candidate['registered_at'] === 'string' &&
    Array.isArray(candidate['controls']) &&
    (candidate['controls'] as readonly unknown[]).every((item) => isControl(item))
  );
}

export interface EvidencePack {
  readonly id: string;
  readonly framework_code: string;
  readonly framework_version: string;
  readonly period_start: string;
  readonly period_end: string;
  /**
   * The token subject — the same string the audit chain records, so "who generated the evidence"
   * and "who did the things it evidences" resolve in one vocabulary.
   */
  readonly generated_by: string;
  readonly generated_at: string;
  /** SHA-256 of the stored pack bytes, served verbatim by the download route. */
  readonly checksum: string;
  /**
   * Always present, `null` when the deployment has no signing key. Honest absence: the hashes
   * hold, the origin is unproven — mirroring UNSEALED audit days.
   */
  readonly signature: string | null;
  readonly signing_key: string | null;
}

export function isEvidencePack(value: unknown): value is EvidencePack {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['framework_code'] === 'string' &&
    typeof candidate['framework_version'] === 'string' &&
    typeof candidate['period_start'] === 'string' &&
    typeof candidate['period_end'] === 'string' &&
    typeof candidate['generated_by'] === 'string' &&
    typeof candidate['generated_at'] === 'string' &&
    typeof candidate['checksum'] === 'string' &&
    (typeof candidate['signature'] === 'string' || candidate['signature'] === null) &&
    (typeof candidate['signing_key'] === 'string' || candidate['signing_key'] === null)
  );
}

export interface ControlCoverage {
  readonly control_code: string;
  /**
   * What was measured, never a verdict. `observed` — every dataset-scoped kind this control names
   * has a reading here. `partial` — some do. `not_observed` — none do, which means unexamined
   * rather than found wanting. `estate_only` — the control names no dataset-scoped kind, so there
   * is nothing per-dataset to report.
   */
  readonly state: 'observed' | 'partial' | 'not_observed' | 'estate_only';
  readonly observed: readonly EvidenceKind[];
  /**
   * The control's dataset-scoped kinds with no reading for this dataset. The actionable half: this
   * is what somebody would go and do.
   */
  readonly missing: readonly EvidenceKind[];
  /**
   * The control's estate-scoped kinds, answered once for the tenant rather than per dataset.
   * Present so a reader can see the control is broader than what this row measures.
   */
  readonly estate_kinds: readonly EvidenceKind[];
}

export function isControlCoverage(value: unknown): value is ControlCoverage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['control_code'] === 'string' &&
    (['observed', 'partial', 'not_observed', 'estate_only'] as readonly unknown[]).includes(candidate['state']) &&
    Array.isArray(candidate['observed']) &&
    (candidate['observed'] as readonly unknown[]).every((item) => isEvidenceKind(item)) &&
    Array.isArray(candidate['missing']) &&
    (candidate['missing'] as readonly unknown[]).every((item) => isEvidenceKind(item)) &&
    Array.isArray(candidate['estate_kinds']) &&
    (candidate['estate_kinds'] as readonly unknown[]).every((item) => isEvidenceKind(item))
  );
}

export interface DatasetCoverage {
  readonly dataset_id: string;
  readonly qualified_name: string;
  readonly controls: readonly ControlCoverage[];
}

export function isDatasetCoverage(value: unknown): value is DatasetCoverage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['qualified_name'] === 'string' &&
    Array.isArray(candidate['controls']) &&
    (candidate['controls'] as readonly unknown[]).every((item) => isControlCoverage(item))
  );
}

export interface CoveragePage {
  readonly items: readonly DatasetCoverage[];
  readonly meta: Readonly<Record<string, unknown>>;
}

export function isCoveragePage(value: unknown): value is CoveragePage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isDatasetCoverage(item)) &&
    candidate['meta'] !== undefined
  );
}
